import { useState, useEffect, useRef } from "react";

/* ── Blink sequence ───────────────────────────────────
   blink 3× → pause → blink → break → blink → lock
───────────────────────────────────────────────────── */

const SEQUENCE = [
  ["b1",      180,  280],   // ① blink
  ["s1",      900,  1100],  // image visible + TV sweep line ↓
  ["b2",      180,  280],   // ② blink
  ["s2",      900,  1100],  // image visible + TV sweep line ↓
  ["b3",      150,  250],   // ③ blink — 3 rapid blinks done
  ["v3",      700,  900],   // take a moment
  ["b4",      200,  300],   // blink one time
  ["v4",      600,  800],   // one more break
  ["b5",      200,  300],   // blink one more time
];

function rand(min, max) { return min + Math.random() * (max - min); }

// Module-level flag — persists across route changes, resets on page refresh
let effectPlayed = false;

export default function GlitchPortrait({ src, alt }) {
  const [phase, setPhase] = useState(effectPlayed ? "stable" : "loading");
  const [settled, setSettled] = useState(effectPlayed);
  const imgRef = useRef(null);

  // Mark as played on every mount (only first matters)
  useEffect(() => {
    effectPlayed = true;
  }, []);

  // If image is already cached, onLoad may not fire — detect it
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      handleImageLoad();
    }
  }, []);

  // State machine
  useEffect(() => {
    if (settled || phase === "stable" || phase === "loading") return;

    const idx = SEQUENCE.findIndex((s) => s[0] === phase);
    if (idx === -1) return;

    if (idx >= SEQUENCE.length - 1) {
      const t = setTimeout(() => setSettled(true), 50);
      return () => clearTimeout(t);
    }

    const [, min, max] = SEQUENCE[idx];
    const t = setTimeout(() => setPhase(SEQUENCE[idx + 1][0]), rand(min, max));
    return () => clearTimeout(t);
  }, [phase, settled]);

  // Track mount time so we start after boot sequence + exit
  const mountTime = useRef(performance.now());

  const handleImageLoad = () => {
    const elapsed = performance.now() - mountTime.current;
    const remaining = Math.max(0, 3200 - elapsed);
    setTimeout(() => {
      setPhase("b1");
    }, remaining);
  };

  // ── RENDER ──

  if (settled) {
    return (
      <div className="portrait-wrap is-settled" style={{ transform: "translateZ(0)" }}>
        <img src={src} alt={alt} className="portrait-photo settled-image" draggable="false" fetchpriority="high" />
      </div>
    );
  }

  // Loading state — image not ready yet
  if (phase === "loading") {
    return (
      <div className="portrait-wrap">
        <div className="crt-scanlines" style={{ opacity: 0.15 }} aria-hidden="true" />
        <div className="crt-noise" style={{ opacity: 0.04 }} aria-hidden="true" />
        <img
          ref={imgRef}
          src={src} alt={alt} className="portrait-photo" draggable="false"
          fetchpriority="high"
          onLoad={handleImageLoad}
        />
      </div>
    );
  }

  // Blink — portrait goes dark momentarily (b1-b5)
  if (phase.startsWith("b")) {
    return (
      <div className="portrait-wrap is-black">
        <div className="crt-noise" style={{ opacity: 0.06 }} aria-hidden="true" />
        <div className="crt-scanlines" style={{ opacity: 0.08 }} aria-hidden="true" />
      </div>
    );
  }

  // Sweep — image visible with TV rolling line
  if (phase.startsWith("s")) {
    return (
      <div className="portrait-wrap">
        <div className="crt-scanlines" style={{ opacity: 0.15 }} aria-hidden="true" />
        <div className="crt-noise" style={{ opacity: 0.04 }} aria-hidden="true" />
        <div className="sweep-line" aria-hidden="true" />
        <img src={src} alt={alt} className="portrait-photo" draggable="false" />
      </div>
    );
  }

  // Visible — image shows clean
  return (
    <div className="portrait-wrap">
      <div className="crt-scanlines" style={{ opacity: 0.15 }} aria-hidden="true" />
      <div className="crt-noise" style={{ opacity: 0.04 }} aria-hidden="true" />
      <img src={src} alt={alt} className="portrait-photo" draggable="false" />
    </div>
  );
}
